// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import {
  DEFAULT_PAGE,
  PAGE_SIZE,
  DATE_FORMAT,
  TEMPORARY_STORAGE_COLORS_PALETTE,
} from 'util/ConstantValues';
import { GetEmissionParams, Site, EmissionStatus } from 'util/Types';
import {
  WorkHistoriesEmissionsData,
  WorkHistoriesParams,
} from 'types/workHistoriesTypes';
import styles from './HistoryEmissions.module.scss';
import { RoleType, PrintLabel } from 'util/Enums';
import { Column } from 'react-table';
import {
  getFormatDateWithCurrentTimezone,
  getUtcFormatDateTime,
} from 'ts/formatDate';
import { getBrightnessFromColor, normalizeNumber } from 'util/commons';
import ResponsiveHistoryTable from 'components/molecules/ResponsiveHistoryTable';
import { getEmissionWorkHistories } from 'apis/work_histories/emission';

export type Props = {
  id?: string;
  sites: Site[];
  isActive: boolean;
  filter?: GetEmissionParams;
  onPrintLabel?: (emission: WorkHistoriesEmissionsData) => void;
};

export const HistoryEmissions = ({
  sites,
  isActive = false,
  filter,
  onPrintLabel,
}: Props) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [data, setData] = useState<WorkHistoriesEmissionsData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [args, setArgs] = useState<WorkHistoriesParams>({
    page: DEFAULT_PAGE,
    take: PAGE_SIZE,
  });
  const isAndroid = /Android/i.test(navigator.userAgent);

  useEffect(() => {
    if ((user.role !== RoleType.AdminOperator && !args.siteId) || !isActive) {
      return;
    }

    getEmissionWorkHistories({
      type: 'requests',
      params: args,
      successCallback: (data) => {
        setTotalRecords(data.total);
        if (args.page > DEFAULT_PAGE) {
          setData((prev) => [...prev, ...(data.emissionsRequest || [])]);
        } else {
          setData(data.emissionsRequest || []);
        }
      },
      failedCallback: () => {
        if (args.page === DEFAULT_PAGE) {
          setData([]);
          setTotalRecords(0);
        }
      },
    });
  }, [args, isActive, user.role]);

  useEffect(() => {
    setArgs((prevArgs) => ({
      ...prevArgs,
      ...filter,
      ...(user.role !== RoleType.AdminOperator && {
        siteId: sites[0]?.childSites[0]?.id,
      }),
      page: DEFAULT_PAGE,
    }));
  }, [sites, filter, user.role]);

  const getStatus = useCallback(
    (emission: WorkHistoriesEmissionsData) => {
      switch (emission.emission.emissionStatus) {
        case EmissionStatus.Requested:
          return (
            <div className={`${styles.emissionType} ${styles.requested}`}>
              {t('history_emission_card.card_discharge_recovery_requested')}
            </div>
          );
        case EmissionStatus.UnRequest:
          return (
            <div className={`${styles.emissionType} ${styles.emission}`}>
              {t('history_emission_card.card_ejected')}
            </div>
          );
        default:
          return (
            <div className={`${styles.emissionType} ${styles.emission}`}>
              {t('history_emission_card.card_closed')}
            </div>
          );
      }
    },
    [t]
  );

  const columns: Column<WorkHistoriesEmissionsData>[] = useMemo(
    () => [
      {
        Header: t('history_emission_card.card_status') || '',
        accessor: (row) => row.emission.emissionStatus,
        Cell: ({ row }) => getStatus(row.original),
      },
      {
        Header: t('history_emission_card.card_discharge_date_time') || '',
        accessor: (row) => row.emission.datetime,
        Cell: ({ value }) => (
          <>{getFormatDateWithCurrentTimezone(value, DATE_FORMAT.slaYMDHm)}</>
        ),
      },
      {
        Header: t('history_emission_card.card_site') || '',
        accessor: (row) => row.emission.site,
        Cell: ({ value }) => <>{value}</>,
      },
      ...(user.role !== RoleType.AdminTenant
        ? [
            {
              Header: t('history_emission_card.card_ejector') || '',
              accessor: (row: WorkHistoriesEmissionsData) =>
                row.emission.tenant,
              Cell: ({ value }: { value: string }) => <>{value}</>,
            },
          ]
        : []),
      {
        Header: t('history_emission_card.card_content') || '',
        accessor: (row) => row.emission.item,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('history_emission_card.card_weight') || '',
        accessor: (row) => row.emission.weight,
        Cell: ({ value }) => <>{normalizeNumber({ value })}kg</>,
      },
      ...(user.role !== RoleType.AdminTenant &&
      (user.role === RoleType.AdminOperator || user.temporaryStorageEnabled)
        ? [
            {
              Header: t('history_emission_card.card_temporary_storage') || '',
              accessor: (row) => row.emission.temporaryStorage?.name,
              Cell: ({ row }) => {
                if (!row.original.emission.temporaryStorage) {
                  return <>-</>;
                }

                const color =
                  TEMPORARY_STORAGE_COLORS_PALETTE[
                    Number(row.original.emission.temporaryStorage.color) %
                      TEMPORARY_STORAGE_COLORS_PALETTE.length
                  ];

                return (
                  <span
                    className={styles.temporaryStorageTag}
                    style={{
                      color:
                        getBrightnessFromColor(color) <= 100
                          ? '#ffffff'
                          : '#1b1b1b',
                      backgroundColor: color,
                    }}
                  >
                    {row.original.emission.temporaryStorage?.name}
                  </span>
                );
              },
            },
            {
              Header: t('emission_qr_scanning.storage_updated_at') || '',
              accessor: (row) => row.emission.storageUpdatedAt,
              Cell: ({ value }) => (
                <>
                  {value
                    ? getFormatDateWithCurrentTimezone(
                        value,
                        DATE_FORMAT.slaYMDHm
                      )
                    : '-'}
                </>
              ),
            },
          ]
        : []),
      {
        Header: t('history_emission_card.card_registration_id') || '',
        accessor: 'id',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header:
          t('history_emission_card.card_collection_request_date_time') || '',
        accessor: (row) => row.request?.datetime || '',
        Cell: ({ row }) => (
          <>
            {row.original.request && row.original.request.datetime !== ''
              ? getUtcFormatDateTime(
                  row.original.request.datetime,
                  DATE_FORMAT.slaYMDHm
                )
              : '-'}
          </>
        ),
      },
      {
        Header: t('history_emission_card.card_collection_requester') || '',
        accessor: (row) => row.request?.userId || '',
        Cell: ({ row }) => (
          <>
            {row.original.request && row.original.request.userId !== ''
              ? `${row.original.request.userId} (${row.original.request.userName})`
              : '-'}
          </>
        ),
      },
      {
        Header: t('history_emission_card.card_export') || '',
        accessor: (row: WorkHistoriesEmissionsData) => row.id,
        Cell: ({ row }: { row: { original: WorkHistoriesEmissionsData } }) => (
          <>
            {onPrintLabel &&
              (row.original.emission.emissionStatus ===
                EmissionStatus.Requested ||
              row.original.emission.emissionStatus ===
                EmissionStatus.UnRequest ? (
                <div className={styles.printLabelButton}>
                  {user.printLabel === PrintLabel.QRCode ? (
                    <button onClick={() => onPrintLabel(row.original)}>
                      {t('history_emission_card.card_print_qr_label_button')}
                    </button>
                  ) : isAndroid ? (
                    <button onClick={() => onPrintLabel(row.original)}>
                      {t('history_emission_card.card_print_label_button')}
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className={styles.printLabelButton}>
                  {row.original.emission.qrCode !== undefined ? (
                    <button onClick={() => onPrintLabel(row.original)}>
                      {t('history_emission_card.card_print_qr_label_button')}
                    </button>
                  ) : isAndroid ? (
                    <button onClick={() => onPrintLabel(row.original)}>
                      {t('history_emission_card.card_print_label_button')}
                    </button>
                  ) : null}
                </div>
              ))}
          </>
        ),
      },
    ],
    [t, user.role, user.printLabel, onPrintLabel, getStatus, data]
  );

  return (
    <div className={styles.historyEmissions}>
      <ResponsiveHistoryTable<WorkHistoriesEmissionsData>
        data={data}
        columns={columns}
        noDataMessage={t('messages.M_008') ?? ''}
        onLoadMore={() => {
          if (
            data.length >= PAGE_SIZE &&
            data.length < (totalRecords || PAGE_SIZE)
          ) {
            setArgs({
              ...args,
              page: args.page + 1,
            });
          }
        }}
        scrollResetDependencies={[filter]}
      />
    </div>
  );
};
